import React, {
    FC, useState, useEffect, useContext, useCallback, useId, useMemo,
} from 'react';
import {
    useQuery, useMutation, useFragment, gql,
} from '@apollo/client';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import { Portal } from 'react-portal';
import DELVIERY_BASKET_QUERY from 'app/graphql/network/basket/deliveryBasketQuery';
import BASKET_QUERY from 'app/graphql/network/basket/basketQuery';
import { ADD_ITEM_TO_CART } from 'app/graphql/network/basket/basketMutations';

import { useRouterState } from 'app/hooks/useRouterState';

import { UIToggleSwitch } from 'app/components/ui';

import { isDesktop } from 'app/utils/resolution';
import { DialogStatic } from 'app/components/Dialog/BR1156/DialogStatic';
import { makePrimetiveSection } from 'app/views/Basket/BasketListArea';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { period } from 'global/const/simple';
import {
    MUTATE_CART_FROM_DELIVERY_BASKET,
} from './graphql/addToDeliveryCart.graphql';

import css from './TableSetting.module.scss';

import TableSettingIcon from './img/TableSettingIcon.component.svg';

interface IProps {
    show: boolean,
    hasRootClass?: boolean
}

// MOCK
export const tableSettingDishId = '4847';
export const TableSetting: FC = ({
    show,
    hasRootClass = true,
}: IProps) => {
    const deliveryId = window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e)));

    const searchParams = new URLSearchParams(window.location.search);
    const paymentMethod = searchParams.get('payment');

    const CURRENT_BASKET_QUERY = deliveryId ? DELVIERY_BASKET_QUERY : BASKET_QUERY;

    const [routerState] = useRouterState();
    const { history } = routerState;
    const { isCartUpdating } = useCartMutationInBasket(deliveryId ? { delivery_Id: deliveryId } : null);

    const [isDialogOpenned, setIsDialogOpenned] = useState<boolean>(false);
    const [isDesktopResolution, setIsDesktopResolution] = useState<boolean>(isDesktop());
    // const { locale } = useContext(LocaleContext);
    const labelId = useId();
    const { data } = useQuery(
        gql`
                query DISH_DATA(
                    $id: ID!,
                    $period: String!,
                ) {
                    menuDish(id: $id, period: $period) {
                        id,
                        title,
                        caption,
                        price,
                        portions,
                        previewMobileImage,
                    }
                }
            `,
        {
            variables: {
                id: tableSettingDishId,
                period: selectedPeriodVar(),
            },
            fetchPolicy: 'cache-first',
        },
    ) || {};

    const { data: cartData, refetch } = useQuery(
        CURRENT_BASKET_QUERY,
        {
            variables: {
                period: selectedPeriodVar(),
                deliveryId: deliveryId || undefined,
            },
            context: {
                message: 'TableSetting',
            },
        },
    );

    const sections = useMemo(
        () => cartData?.cart?.sections,
        [cartData],
    );
    const [addTableSettingToCart] = useMutation(ADD_ITEM_TO_CART);

    const [addTableSettingToDeliveryCart] = useMutation(MUTATE_CART_FROM_DELIVERY_BASKET);

    const primetiveSection = useMemo(
        () => makePrimetiveSection(sections),
        [sections],
    );

    const hasTableSettingDishInBasket = useMemo(
        () => primetiveSection
            .some((dishWithType: any) => dishWithType
                .includes(tableSettingDishId)),
        [primetiveSection],
    );

    const [isChecked, setIsChecked] = useState<boolean>(hasTableSettingDishInBasket);

    const toggleTableSettingForDeliverySwitch = useCallback(
        () => {
            if (hasTableSettingDishInBasket) {
                addTableSettingToDeliveryCart({
                    variables: {
                        dishes: [{
                            dish_id: tableSettingDishId,
                            portions: 0,
                        }],
                        period: selectedPeriodVar(),
                        delivery_id: deliveryId,
                    },
                }).then(() => {
                    refetch();
                });
                setIsChecked(false);
                return;
            }
            addTableSettingToDeliveryCart({
                variables: {
                    dishes: [{
                        dish_id: tableSettingDishId,
                        portions: 1,
                    }],
                    period: selectedPeriodVar(),
                    delivery_id: deliveryId,
                },
            }).then(() => {
                setIsChecked(true);
                refetch();
            });
        },
        [
            deliveryId,
            hasTableSettingDishInBasket,
            addTableSettingToDeliveryCart,
            refetch,
        ],
    );

    const toggleTableSettingSwitch = useCallback(
        () => {
            if (hasTableSettingDishInBasket) {
                addTableSettingToCart(
                    {
                        variables: {
                            dish_id: tableSettingDishId,
                            period: selectedPeriodVar(),
                            portions: 0,
                        },
                    },
                ).then(() => {
                    refetch();
                });
                setIsChecked(false);
                return;
            }
            addTableSettingToCart(
                {
                    variables: {
                        dish_id: tableSettingDishId,
                        period: selectedPeriodVar(),
                        portions: 1,
                    },
                },
            ).then(() => {
                setIsChecked(true);
                refetch();
            });
        },
        [
            hasTableSettingDishInBasket,
            addTableSettingToCart,
            refetch,
        ],
    );

    const checkDesktopResolution = useCallback(
        () => setIsDesktopResolution(isDesktop()),
        [],
    );

    const onReject = () => {
        setIsDialogOpenned(false);
        // history.push('/menu');
    };

    useEffect(
        () => {
            window.addEventListener(
                'resize',
                checkDesktopResolution,
            );
            return () => {
                window.removeEventListener('resize', checkDesktopResolution);
            };
        },
        [checkDesktopResolution],
    );

    useEffect(
        () => {
            setIsChecked(hasTableSettingDishInBasket);
        },
        [hasTableSettingDishInBasket],
    );

    const price = useMemo(() => {
        const dv = paymentMethod === 'recurrent' ? 10 : 0;
        // eslint-disable-next-line no-unsafe-optional-chaining, no-mixed-operators
        const discount = data?.menuDish?.price / 100 * dv;
        // eslint-disable-next-line no-unsafe-optional-chaining
        return data?.menuDish?.price - discount;
    }, [data?.menuDish?.price, paymentMethod]);

    const confirmText = useMemo(
        () => (hasTableSettingDishInBasket ? 'Удалить из корзины' : `Добавить за ${price} ₽`),
        [price, hasTableSettingDishInBasket],
    );

    if (!show) {
        return null;
    }

    return (
        <div className={hasRootClass
            ? css.tableSettingRowRoot
            : ''}
        >
            <div className={css.tableSettingRowFlexContainer}>
                <div className={css.tableSettingRowContent}>
                    <div className={css.tableSettingRowContentIcon}>
                        <TableSettingIcon />
                    </div>
                    <div className={css.tableSettingRowContentText}>
                        Праздничная
                        <br />
                        <span
                            role="none"
                            onClick={() => setIsDialogOpenned(true)}
                            className={css.tableSettingRowTextDecoration}
                        >
                            сервировка
                        </span>
                    </div>
                </div>
                <div className={css.tableSettingRowToggleWrapper}>
                    <div className={css.tableSettingPriceRow}>
                        +
                        {price}
                        {' '}
                        ₽
                    </div>
                    <UIToggleSwitch
                        onChanegHandler={deliveryId ? toggleTableSettingForDeliverySwitch : toggleTableSettingSwitch}
                        isChecked={isChecked}
                        id={labelId}
                        isDisabled={isCartUpdating}
                    />
                </div>
                <Portal>
                    <DialogStatic
                        rejectText=""
                        confirmText={confirmText}
                        isOpen={isDialogOpenned}
                        onConfirm={() => {
                            if (deliveryId) {
                                toggleTableSettingForDeliverySwitch();
                            }
                            toggleTableSettingSwitch();
                            setIsDialogOpenned(false);
                        }}
                        onReject={onReject}
                        textAlign="start"
                        // @ts-ignore
                        responisve={!isDesktopResolution}
                        onlyConfirmButton
                        isProfileLayout
                        isNewIrresponsiveLayout
                        oneRowButtons
                        buttonsReversed
                    >
                        <>
                            <div className={css.tableSettingModalTitle}>
                                Праздничная
                                {isDesktopResolution ? ' ' : <br />}
                                сервировка
                            </div>
                            <img
                                className={css.tableSettingDialogImage}
                                src={require('./img/table_setting-s.jpg')}
                                alt="table setting"
                            />
                            <ul className={css.tableSettingModalList}>
                                <li>
                                    мини-игра, чтобы узнать друг друга получше
                                </li>
                                <li>
                                    салфетки-сердечки для сервировки
                                </li>
                                <li>
                                    бенгальские огни
                                </li>
                            </ul>
                        </>
                    </DialogStatic>
                </Portal>
            </div>
        </div>
    );
};
